// src/components/Home/Section1.jsx
import React, { useEffect, useMemo, useState } from 'react';
import BgImg from '../../assets/img/bg-s1.png';
import Logo from '../../assets/icons/logo-1.svg';
import Marquee from 'react-fast-marquee';

// Opciones mobile
import Option1Mobile from '../../assets/img/option1-s1-mobile.png';
import Option1MobileWebp from '../../assets/img/option1-s1-mobile.webp';
import Option2Mobile from '../../assets/img/option2-s1-mobile.png';
import Option2MobileWebp from '../../assets/img/option2-s1-mobile.webp';
import Option3Mobile from '../../assets/img/option3-s1-mobile.png';
import Option3MobileWebp from '../../assets/img/option3-s1-mobile.webp';

// Carrusel
import C1 from '../../assets/img/section1/img-1.png';
import C2 from '../../assets/img/section1/img-2.png';
import C3 from '../../assets/img/section1/img-3.png';
import C4 from '../../assets/img/section1/img-4.png';
import C5 from '../../assets/img/section1/img-5.png';
import C6 from '../../assets/img/section1/img-6.png';
import C7 from '../../assets/img/section1/img-7.png';
import C8 from '../../assets/img/section1/img-8.png';
import C9 from '../../assets/img/section1/img-9.png';
import C10 from '../../assets/img/section1/img-10.png';

const carouselImgs = [C1, C2, C3, C4, C5, C6, C7, C8, C9, C10];

/** Hook: detecta mobile inmediato */
function useIsMobile() {
  const get = () =>
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 640px)').matches
      : false;
  const [isMobile, setIsMobile] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange);
    };
  }, []);
  return isMobile;
}

/** Imagen WebP + fallback */
const SmartImg = ({
  srcWebp,
  src,
  alt,
  className = '',
  draggable = false,
  loading = 'lazy',
  decoding = 'async',
  fetchPriority,
}) => (
  <picture className="block w-full h-full">
    {srcWebp && <source srcSet={srcWebp} type="image/webp" />}
    <img
      src={src}
      alt={alt}
      className={className}
      draggable={draggable}
      loading={loading}
      decoding={decoding}
      fetchPriority={fetchPriority}
    />
  </picture>
);

const Section1 = () => {
  const isMobile = useIsMobile();
  const speed = useMemo(() => (isMobile ? 60 : 30), [isMobile]); // más rápido en mobile

  const handleScroll = (id) => {
    const target = document.getElementById(id);
    if (target) target.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className="
        relative min-h-[100svh] w-full
        bg-cover bg-center bg-no-repeat
        bg-scroll md:bg-fixed
        flex overflow-y-auto overflow-x-hidden
      "
      style={{ backgroundImage: `url(${BgImg})` }}
    >
      {/* Overlay inferior */}
      <div
        className="absolute bottom-0 left-0 w-full h-[30%] z-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(15,15,15,0.65) 100%)',
        }}
      />

      {/* Contenido */}
      <div className="relative z-10 max-w-[900px] w-full mx-auto px-5 sm:px-6 flex flex-col justify-center md:justify-end">
        <div
          className="
            flex flex-col items-center text-center
            gap-4 sm:gap-6
            pt-[max(env(safe-area-inset-top),16px)]
            pb-[clamp(32px,8vh,96px)]
          "
        >
          {/* Logo + título */}
          <div className="flex flex-col items-center">
            <img
              className="w-[170px] sm:w-[300px] mt-[40px] animate-fade-in"
              src={Logo}
              alt="logo"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
            <h1 className="text-[22px] leading-tight sm:text-[28px] md:text-[32px] font-[500] font-bai italic mt-[40px] sm:mt-4 text-gray-200 animate-slide-up">
              Tu socio en Soluciones Industriales,
              <span className="inline sm:hidden"><br/></span> de principio a fin.
            </h1>
          </div>

          {/* Texto destacado */}
          <p className="text-gray-200 text-sm sm:text-base md:text-lg max-w-3xl font-bai  w-[80%] md:w-full mx-auto mt-4 leading-relaxed px-2">
            Acompañamos cada proyecto con un servicio de{' '}
            <span className="font-semibold">Ingeniería Integral:</span><br />
            relevamiento en terreno, diseño, fabricación y puesta a punto en planta.
          </p>

          {/* Opciones */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mt-6">
            <button onClick={() => handleScroll('nosotros')} className="animate-pop [animation-delay:100ms]">
              <SmartImg
                srcWebp={Option1MobileWebp}
                src={Option1Mobile}
                alt="Ir a Nosotros"
                className="w-[180px] sm:w-[210px] md:w-[230px] h-auto rounded-[10px] cursor-pointer md:hover:scale-105 transition-transform"
              />
            </button>
            <button onClick={() => handleScroll('que-hacemos')} className="animate-pop [animation-delay:200ms]">
              <SmartImg
                srcWebp={Option2MobileWebp}
                src={Option2Mobile}
                alt="Ir a Qué Hacemos"
                className="w-[180px] sm:w-[210px] md:w-[230px] h-auto rounded-[10px] cursor-pointer md:hover:scale-105 transition-transform"
              />
            </button>
            <button onClick={() => handleScroll('proyectos-clientes')} className="animate-pop [animation-delay:300ms]">
              <SmartImg
                srcWebp={Option3MobileWebp}
                src={Option3Mobile}
                alt="Ir a Proyectos y Clientes"
                className="w-[180px] sm:w-[210px] md:w-[230px] h-auto rounded-[10px] cursor-pointer md:hover:scale-105 transition-transform"
              />
            </button>
          </div>

          {/* Carrusel infinito con react-fast-marquee */}
          <div className="relative self-center w-screen overflow-hidden mt-8 sm:mt-10">
            {/* Fades laterales (igual que antes) */}
            <div className="pointer-events-none absolute left-0 top-0 h-full w-20 bg-gradient-to-r from-[#0b0a0b]/70 to-transparent z-10" />
            <div className="pointer-events-none absolute right-0 top-0 h-full w-20 bg-gradient-to-l from-[#0b0a0b]/70 to-transparent z-10" />

            <Marquee
              gradient={false}
              speed={speed}
              pauseOnHover={false}
              className="w-full"
            >
              {carouselImgs.map((img, i) => (
                <div
                  key={i}
                  className="w-[150px] h-[138px] sm:w-[177px] sm:h-[165px] flex items-center justify-center flex-shrink-0 mx-4"
                >
                  <img
                    src={img}
                    alt={`carousel-${i}`}
                    className="w-full h-full object-contain"
                    draggable={false}
                  />
                </div>
              ))}
            </Marquee>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Section1;